import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { eachDayOfInterval, format, parseISO } from "date-fns";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  advanceBusinessReportSelector,
  fetchOrganicRankTrackingData,
  fetchSponsoredRankTrackingData,
} from "../../../../../../redux/slices/advance_business_report";
import { TabPanel } from "../../../helpers";

import RankTracking from "./RankTracking";
import {
  buildDateKeys,
  createRankLookup,
  createWeightedRankDateIndex,
  downloadCsv,
  findWeightedRankDateIndex,
  getFirstRankForTerm,
  getRankForDateKeys,
  getUniqueSearchTerms,
} from "./rankTrackingUtils";

const getDateHeader = (date) => format(date, "dd/MM");

const getSearchTermSummaries = (ranks, sponsoredRanks) => {
  const organicLookup = createRankLookup(ranks);
  const sponsoredLookup = createRankLookup(sponsoredRanks);

  const summaries = getUniqueSearchTerms(ranks, sponsoredRanks).map(
    (searchTerm) => {
      const organic = getFirstRankForTerm(organicLookup, searchTerm);
      const sponsored = getFirstRankForTerm(sponsoredLookup, searchTerm);
      return {
        keywords: searchTerm,
        sv1w: organic?.search_volume_1w || sponsored?.search_volume_1w || "-",
        relevancy: organic?.relevancy || sponsored?.relevancy || "-",
      };
    }
  );

  summaries.sort((a, b) => Number(b.sv1w) - Number(a.sv1w));

  return { summaries, organicLookup, sponsoredLookup };
};

const buildRankCsvRows = ({
  source,
  asinLabel,
  ranks,
  sponsoredRanks,
  dateHeaders,
  dateKeys,
}) => {
  const { summaries, organicLookup, sponsoredLookup } = getSearchTermSummaries(
    ranks,
    sponsoredRanks
  );

  return summaries.map((term) => {
    const row = {
      Source: source,
      ASIN: asinLabel || "",
      Keywords: term.keywords,
      "SV (1w)": term.sv1w,
      Rlv: term.relevancy,
    };

    dateHeaders.forEach((dateHeader, index) => {
      const matchingRank = getRankForDateKeys(
        organicLookup,
        term.keywords,
        dateKeys[index]
      );
      const matchingSponsoredRank = getRankForDateKeys(
        sponsoredLookup,
        term.keywords,
        dateKeys[index]
      );
      row[dateHeader] = matchingRank?.position || "-";
      row[`${dateHeader} Spon`] = matchingSponsoredRank?.position || "-";
    });

    return row;
  });
};

const buildWeightedRankCsvRow = ({
  asinLabel,
  weightedRankData,
  allDates,
  dateHeaders,
}) => {
  if (!weightedRankData?.dates || !weightedRankData?.weightedRank) {
    return null;
  }

  const row = {
    Source: "Rank Tracking",
    ASIN: asinLabel || "",
    Keywords: "Weighted Rank",
    "SV (1w)": "-",
    Rlv: "-",
  };

  const weightedDateIndex = createWeightedRankDateIndex(weightedRankData.dates);

  dateHeaders.forEach((dateHeader, index) => {
    const dateIndex = findWeightedRankDateIndex(
      weightedDateIndex,
      allDates[index]
    );
    const weightedValue =
      dateIndex !== -1 ? weightedRankData.weightedRank[dateIndex] : null;

    row[dateHeader] =
      weightedValue !== null && weightedValue !== undefined
        ? Number(weightedValue).toFixed(1)
        : "-";
    row[`${dateHeader} Spon`] = "-";
  });

  return row;
};

const buildRankTrackingCsvExport = ({
  asin,
  marketplace,
  organicData,
  sponsoredData,
  weightedRankData,
}) => {
  if (!organicData?.start_date || !organicData?.end_date) {
    return { headers: [], data: [] };
  }

  const startDate = parseISO(organicData.start_date);
  const endDate = parseISO(organicData.end_date);
  const allDates = eachDayOfInterval({ start: startDate, end: endDate });

  const dateHeaders = allDates.map(getDateHeader);
  const dateKeys = buildDateKeys(allDates, "day");

  const headers = [
    { label: "Source", key: "Source" },
    { label: "ASIN", key: "ASIN" },
    { label: "Keywords", key: "Keywords" },
    { label: "SV (1w)", key: "SV (1w)" },
    { label: "Rlv", key: "Rlv" },
    ...dateHeaders.flatMap((dateHeader) => [
      { label: dateHeader, key: dateHeader },
      { label: "Spon", key: `${dateHeader} Spon` },
    ]),
  ];

  const ownRanks = organicData?.ranks?.ranks || [];
  const ownSponsoredRanks = sponsoredData?.ranks?.ranks || [];

  const rows = [
    ...buildRankCsvRows({
      source: "Rank Tracking",
      asinLabel: asin,
      ranks: ownRanks,
      sponsoredRanks: ownSponsoredRanks,
      dateHeaders,
      dateKeys,
    }),
  ];

  if (marketplace !== "ca") {
    const weightedRow = buildWeightedRankCsvRow({
      asinLabel: asin,
      weightedRankData,
      allDates,
      dateHeaders,
    });
    if (weightedRow) {
      rows.unshift(weightedRow);
    }
  }

  if (marketplace === "us") {
    const competitorRanksByAsin =
      organicData?.ranks?.competitors_ranks?.ranks || {};
    const competitorSponsoredByAsin =
      sponsoredData?.ranks?.competitors_ranks?.ranks || {};
    const competitorAsins = [
      ...new Set([
        ...Object.keys(competitorRanksByAsin),
        ...Object.keys(competitorSponsoredByAsin),
      ]),
    ].sort();

    competitorAsins.forEach((competitorAsin) => {
      rows.push(
        ...buildRankCsvRows({
          source: "Competitors",
          asinLabel: competitorAsin,
          ranks: competitorRanksByAsin[competitorAsin] || [],
          sponsoredRanks: competitorSponsoredByAsin[competitorAsin] || [],
          dateHeaders,
          dateKeys,
        })
      );
    });
  }

  return { headers, data: rows };
};

const RankTrackingModalPopup = ({
  openModal,
  handleCloseModal,
  asin,
  leaf,
  currentStore,
  currentStartDate,
  currentEndDate,
  marketplace,
  weightedRankData,
}) => {
  const [tabValue, setTabValue] = useState(0);
  // Exact-match keyword filter shared by both tabs; see parseKeywordFilter.
  const [keywordFilter, setKeywordFilter] = useState("");

  // Draggable modal state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });

  // Resizable modal state
  const [size, setSize] = useState({ width: 90, height: 90 }); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartPos = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const resizeDirection = useRef(null);

  // Reset position, size and the keyword filter when modal opens
  useEffect(() => {
    if (openModal) {
      setPosition({ x: 0, y: 0 });
      setSize({ width: 90, height: 90 });
      setKeywordFilter("");
    }
  }, [openModal]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleResizeMouseDown = (e, direction) => {
    e.stopPropagation();
    setIsResizing(true);
    resizeDirection.current = direction;
    resizeStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      width: (size.width / 100) * window.innerWidth,
      height: (size.height / 100) * window.innerHeight,
    };
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStartPos.current.x,
          y: e.clientY - dragStartPos.current.y,
        });
      }
      if (isResizing) {
        const deltaX = e.clientX - resizeStartPos.current.x;
        const deltaY = e.clientY - resizeStartPos.current.y;
        const direction = resizeDirection.current;

        let newWidth = resizeStartPos.current.width;
        let newHeight = resizeStartPos.current.height;

        if (direction.includes("e")) {
          newWidth = resizeStartPos.current.width + deltaX * 2;
        }
        if (direction.includes("w")) {
          newWidth = resizeStartPos.current.width - deltaX * 2;
        }
        if (direction.includes("s")) {
          newHeight = resizeStartPos.current.height + deltaY * 2;
        }
        if (direction.includes("n")) {
          newHeight = resizeStartPos.current.height - deltaY * 2;
        }

        // Convert to percentage and enforce min/max
        const widthPercent = Math.min(
          98,
          Math.max(40, (newWidth / window.innerWidth) * 100)
        );
        const heightPercent = Math.min(
          98,
          Math.max(30, (newHeight / window.innerHeight) * 100)
        );

        setSize({ width: widthPercent, height: heightPercent });
      }
    },
    [isDragging, isResizing]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    resizeDirection.current = null;
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Resize handle styles
  const resizeHandleStyle = {
    position: "absolute",
    zIndex: 1200,
  };

  const cornerStyle = {
    ...resizeHandleStyle,
    width: 16,
    height: 16,
  };

  const edgeStyleHorizontal = {
    ...resizeHandleStyle,
    height: 8,
    left: 16,
    right: 16,
  };

  const edgeStyleVertical = {
    ...resizeHandleStyle,
    width: 8,
    top: 16,
    bottom: 16,
  };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const tabsProps = (index) => {
    return {
      id: `rank-tracking-tab-${index}`,
      "aria-controls": `rank-tracking-tabpanel-${index}`,
    };
  };

  const {
    sponsoredRankTrackingData,
    organicRankTrackingData,
    sponsoredRankTrackingDataLoading,
    organicRankTrackingDataLoading,
    caSponsoredRankTrackingData,
    caOrganicRankTrackingData,
    caSponsoredRankTrackingDataLoading,
    caOrganicRankTrackingDataLoading,
  } = useSelector(advanceBusinessReportSelector);
  const dispatch = useDispatch();

  // Normalize leaf to handle both boolean and "yes" string
  const isLeaf = leaf === "yes" || leaf === true;

  const isRankDataLoading =
    sponsoredRankTrackingDataLoading ||
    organicRankTrackingDataLoading ||
    caSponsoredRankTrackingDataLoading ||
    caOrganicRankTrackingDataLoading;

  const organicExportData =
    marketplace === "us" ? organicRankTrackingData : caOrganicRankTrackingData;
  const sponsoredExportData =
    marketplace === "us"
      ? sponsoredRankTrackingData
      : caSponsoredRankTrackingData;

  // Built only when the user asks for it: the export walks every competitor ASIN
  // across every day, which is far too expensive to run on each render.
  const canDownloadCsv = Boolean(
    organicExportData?.start_date &&
      organicExportData?.end_date &&
      (organicExportData?.ranks?.ranks?.length ||
        sponsoredExportData?.ranks?.ranks?.length)
  );

  const handleDownloadCsv = useCallback(() => {
    const { headers, data } = buildRankTrackingCsvExport({
      asin,
      marketplace,
      organicData: organicExportData,
      sponsoredData: sponsoredExportData,
      weightedRankData,
    });

    if (data.length === 0) return;

    downloadCsv({
      filename: `${asin}_rank_tracking.csv`,
      headers,
      rows: data,
    });
  }, [
    asin,
    marketplace,
    organicExportData,
    sponsoredExportData,
    weightedRankData,
  ]);

  // Wrap dataFetching in useCallback to stabilize its reference
  const dataFetching = useCallback(() => {
    let store = currentStore.replace("_client", "");

    dispatch(
      fetchOrganicRankTrackingData({
        asin: isLeaf ? "" : asin,
        child_asin: isLeaf ? asin : "",
        startDate: currentStartDate,
        endDate: currentEndDate,
        periodicity: "day",
        store: store,
        marketPlace: marketplace,
      })
    );
    dispatch(
      fetchSponsoredRankTrackingData({
        asin: isLeaf ? "" : asin,
        child_asin: isLeaf ? asin : "",
        startDate: currentStartDate,
        endDate: currentEndDate,
        periodicity: "day",
        store: store,
        marketPlace: marketplace,
      })
    );
  }, [
    dispatch,
    asin,
    isLeaf,
    currentStartDate,
    currentEndDate,
    currentStore,
    marketplace,
  ]);

  // Prevent duplicate fetches during a single open cycle
  const lastFetchKeyRef = useRef(null);
  useEffect(() => {
    if (openModal) {
      const fetchKey = [
        isLeaf ? "child" : "parent",
        asin,
        currentStartDate,
        currentEndDate,
        currentStore,
      ].join("|");

      if (lastFetchKeyRef.current !== fetchKey) {
        dataFetching();
        lastFetchKeyRef.current = fetchKey;
      }
    } else {
      lastFetchKeyRef.current = null;
    }
  }, [
    openModal,
    dataFetching,
    isLeaf,
    asin,
    currentStartDate,
    currentEndDate,
    currentStore,
  ]);

  return (
    <Modal
      open={openModal}
      onClose={handleCloseModal}
      aria-labelledby="rank-tracking-modal"
      aria-describedby="rank-tracking-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "52%",
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          width: `${size.width}%`,
          height: `${size.height}%`,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 6,
          borderRadius: 4,
          zIndex: 1000,
          overflow: "hidden",
        }}
      >
        {/* Resize handles */}
        {/* Corners */}
        <Box
          onMouseDown={(e) => handleResizeMouseDown(e, "nw")}
          sx={{ ...cornerStyle, top: 0, left: 0, cursor: "nw-resize" }}
        />
        <Box
          onMouseDown={(e) => handleResizeMouseDown(e, "ne")}
          sx={{ ...cornerStyle, top: 0, right: 0, cursor: "ne-resize" }}
        />
        <Box
          onMouseDown={(e) => handleResizeMouseDown(e, "sw")}
          sx={{ ...cornerStyle, bottom: 0, left: 0, cursor: "sw-resize" }}
        />
        <Box
          onMouseDown={(e) => handleResizeMouseDown(e, "se")}
          sx={{ ...cornerStyle, bottom: 0, right: 0, cursor: "se-resize" }}
        />
        {/* Edges */}
        <Box
          onMouseDown={(e) => handleResizeMouseDown(e, "n")}
          sx={{ ...edgeStyleHorizontal, top: 0, cursor: "n-resize" }}
        />
        <Box
          onMouseDown={(e) => handleResizeMouseDown(e, "s")}
          sx={{ ...edgeStyleHorizontal, bottom: 0, cursor: "s-resize" }}
        />
        <Box
          onMouseDown={(e) => handleResizeMouseDown(e, "w")}
          sx={{ ...edgeStyleVertical, left: 0, cursor: "w-resize" }}
        />
        <Box
          onMouseDown={(e) => handleResizeMouseDown(e, "e")}
          sx={{ ...edgeStyleVertical, right: 0, cursor: "e-resize" }}
        />

        {/* Draggable header bar */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 40,
            cursor: isDragging ? "grabbing" : "grab",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8fafc",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            borderBottom: "1px solid #e2e8f0",
            userSelect: "none",
          }}
        >
          <DragIndicatorIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
        </Box>
        <Box
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            top: 0,
            right: 2,
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 1100,
          }}
        >
          <CloseIcon
            sx={{
              color: "#64748B",
              fontSize: 24,
              cursor: "pointer",
            }}
          />
        </Box>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={"Rank Tracking"} {...tabsProps(0)} />
            <Tab label={"Competitors"} {...tabsProps(1)} />
          </Tabs>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              size="small"
              value={keywordFilter}
              onChange={(event) => setKeywordFilter(event.target.value)}
              placeholder="Exact keyword(s), comma-separated"
              inputProps={{ "aria-label": "Filter keywords" }}
              sx={{ width: 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: keywordFilter ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear keyword filter"
                      onClick={() => setKeywordFilter("")}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Tooltip title="Download Rank Tracking + Competitors CSV">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  disabled={isRankDataLoading || !canDownloadCsv}
                  onClick={handleDownloadCsv}
                >
                  Download CSV
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <TabPanel value={tabValue} index={0}>
          {isRankDataLoading ? (
            <>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((item) => (
                <Skeleton
                  key={item}
                  width={"95%"}
                  sx={{ mt: 5, ml: 5 }}
                  variant="rectangular"
                  height={15}
                />
              ))}
            </>
          ) : (
            <RankTracking
              data={organicRankTrackingData}
              sponsoredData={sponsoredRankTrackingData}
              ca_data={caOrganicRankTrackingData}
              caSponsoredData={caSponsoredRankTrackingData}
              currentPeriodicity={"day"}
              marketPlace={marketplace}
              height={"70vh"}
              isModal={true}
              isLeaf={leaf}
              weightedRankData={weightedRankData}
              keywordFilter={keywordFilter}
            />
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {isRankDataLoading ? (
            <>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((item) => (
                <Skeleton
                  key={item}
                  width={"95%"}
                  sx={{ mt: 5, ml: 5 }}
                  variant="rectangular"
                  height={15}
                />
              ))}
            </>
          ) : (
            <RankTracking
              type="competitors"
              data={organicRankTrackingData}
              sponsoredData={sponsoredRankTrackingData}
              ca_data={caOrganicRankTrackingData}
              caSponsoredData={caSponsoredRankTrackingData}
              marketPlace={marketplace}
              currentPeriodicity={"day"}
              height={"65vh"}
              isModal={true}
              weightedRankData={weightedRankData}
              keywordFilter={keywordFilter}
            />
          )}
        </TabPanel>
      </Box>
    </Modal>
  );
};

export default RankTrackingModalPopup;
